import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, RefreshCw, Navigation } from 'lucide-react';

/**
 * LocationCapture — Mendapatkan lokasi GPS saat ini
 * 
 * Props:
 * @param {function} onLocation(lat, lng, address) - Callback saat lokasi didapat
 * @param {boolean} autoCapture - Otomatis capture lokasi saat mount
 */
export default function LocationCapture({ onLocation, autoCapture = true }) {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const onLocationRef = useRef(onLocation);
  const mountedRef = useRef(true);

  // Simpan callback terbaru ke ref (hindari infinite loop)
  useEffect(() => {
    onLocationRef.current = onLocation;
  }, [onLocation]);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const captureLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolokasi tidak didukung browser ini.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        });
        // AbortController timeout
        setTimeout(() => {
          // If still no response, reject
        }, 15000);
      });

      if (!mountedRef.current) return;

      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const acc = pos.coords.accuracy;

      setLocation({ lat, lng, accuracy: Math.round(acc) });

      // Reverse geocode dengan AbortController timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=id`,
          { 
            headers: { 'User-Agent': 'LayananSynexa/1.0' },
            signal: controller.signal
          }
        );
        clearTimeout(timeoutId);

        if (!mountedRef.current) return;
        const data = await res.json();
        const addr = data.display_name || '';
        setAddress(addr);
        if (onLocationRef.current) onLocationRef.current(lat, lng, addr);
      } catch {
        if (mountedRef.current) {
          if (onLocationRef.current) onLocationRef.current(lat, lng, '');
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[Location] Error:', err);
      if (err.code === 1) {
        setError('Izin lokasi ditolak. Izinkan akses lokasi di pengaturan browser.');
      } else if (err.code === 2) {
        setError('Tidak dapat menentukan lokasi. Coba di luar ruangan.');
      } else if (err.code === 3) {
        setError('Waktu habis mencari lokasi. Coba lagi.');
      } else {
        setError('Gagal mendapatkan lokasi: ' + (err.message || 'Unknown error'));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []); // Tidak ada dependency — pakai ref pattern

  useEffect(() => {
    if (autoCapture) {
      captureLocation();
    }
  }, [autoCapture, captureLocation]);

  return (
    <div style={{
      background: 'var(--gray-50)', borderRadius: 'var(--radius)',
      padding: '12px 14px', marginBottom: 12,
      border: error ? '1px solid #fecaca' : location ? '1px solid #d1fae5' : '1px solid var(--gray-200)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <MapPin size={16} color={location ? '#2dce89' : error ? '#f5365c' : 'var(--gray-400)'} />
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)', flex: 1 }}>
          {loading ? 'Mendapatkan lokasi...' :
           location ? '✅ Lokasi ditemukan' :
           error ? '⚠️ ' + error :
           'Lokasi'}
        </span>
        <button
          onClick={captureLocation}
          disabled={loading}
          className="btn-icon"
          title="Refresh lokasi"
          style={{ width: 30, height: 30 }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 0.6s linear infinite' : 'none' }} />
        </button>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
            Mencari sinyal GPS... ({navigator.geolocation ? 'mencari' : 'tidak tersedia'})
          </span>
        </div>
      )}

      {location && (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--gray-500)'
            }}>
              {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </span>
            <span style={{
              fontSize: '0.65rem', color: 'var(--gray-400)',
              background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 4
            }}>
              ±{location.accuracy}m
            </span>
            <Navigation size={12} color="#11cdef" />
          </div>
          {address && (
            <p style={{
              fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 4, lineHeight: 1.4
            }}>
              {address.length > 100 ? address.slice(0, 100) + '...' : address}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
